import { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import DraggableModal from "./DraggableModal";
import { sendEmail, isValidEmail } from "../resendApi";
import { loadFromSupabase } from "../supabaseDB";

/* GFI Nexus — Shared Email composer
   ─────────────────────────────────────────────────────────────────
   Used by ShipmentRegistry (Email Warehouse) and InvoiceRegistry
   (Email Customer). The dialog is presentation-only — the caller
   passes the prepared recipient list, the doc-kind options, and an
   async `buildAttachments(checkedKinds)` that returns
   [{ filename, blob, mimeType }]. The caller also passes a
   `defaultSubject` + `defaultBody` so the parent module can keep
   ownership of the wording.

   Recipient model: each row is { name, email, checked } — the
   distribution-list contacts arrive pre-checked, the user can
   uncheck any. A free-form input below lets the user add extras
   (comma- or newline-separated). Manual entries get folded into
   the same `checked` list before send. */

const T = {
  en: {
    title: "Send email",
    fromAccount: "Send from",
    warehouseRecips: "Warehouse",
    recipients: "Recipients",
    fromDistribution: "From the distribution list",
    addMore: "Add more emails (comma-separated)",
    addMoreHint: "Type one or more emails, separated by commas, spaces, or newlines.",
    docs: "Documents to attach",
    subject: "Subject",
    body: "Message",
    options: "Options",
    attachPdf: "Attach PDF to email",
    sendCopy: "Send myself a copy",
    sendCopyHint: "CC — arrives in your Inbox (not Sent), since this app sends through Resend, not your Google account",
    selectAll: "Select all",
    deselectAll: "Deselect all",
    docType: "Document type",
    signature: "Signature",
    signatureNone: "— No signature —",
    signaturePreview: "Signature preview",
    cancel: "Cancel",
    send: "Send",
    sending: "Sending...",
    sent: "Sent",
    sentMsg: "Email sent successfully",
    error: "Could not send",
    notConfigured: "REACT_APP_GOOGLE_CLIENT_ID not set in .env.local",
    notSignedIn: "Sign in to Google first",
    noRecipients: "Select at least one recipient",
    noAttachments: "Select at least one document",
    invalidEmail: "Invalid email format",
    preparing: "Preparing attachments...",
    preview: "Preview",
    previewTitle: "Email preview",
    previewLoading: "Rendering preview…",
    previewClose: "Close",
  },
  es: {
    title: "Enviar correo",
    fromAccount: "Enviar desde",
    warehouseRecips: "Bodega",
    recipients: "Destinatarios",
    fromDistribution: "De la lista de distribución",
    addMore: "Agregar más correos (separa con comas)",
    addMoreHint: "Escribe uno o más correos separados por comas, espacios o saltos de línea.",
    docs: "Documentos a adjuntar",
    subject: "Asunto",
    body: "Mensaje",
    options: "Opciones",
    attachPdf: "Adjuntar PDF al correo",
    sendCopy: "Enviarme una copia",
    sendCopyHint: "CC — llega a tu Inbox (no a Enviados), ya que la app envía por Resend, no desde tu cuenta de Google",
    selectAll: "Seleccionar todo",
    deselectAll: "Deseleccionar todo",
    docType: "Tipo de documento",
    signature: "Firma",
    signatureNone: "— Sin firma —",
    signaturePreview: "Vista previa de la firma",
    cancel: "Cancelar",
    send: "Enviar",
    sending: "Enviando...",
    sent: "Enviado",
    sentMsg: "Correo enviado exitosamente",
    error: "No se pudo enviar",
    notConfigured: "Falta REACT_APP_GOOGLE_CLIENT_ID en .env.local",
    notSignedIn: "Primero inicia sesión en Google",
    noRecipients: "Selecciona al menos un destinatario",
    noAttachments: "Selecciona al menos un documento",
    invalidEmail: "Formato de correo inválido",
    preparing: "Preparando adjuntos...",
    preview: "Vista previa",
    previewTitle: "Vista previa del correo",
    previewLoading: "Generando vista previa…",
    previewClose: "Cerrar",
  },
};

const inp = { padding: "8px 10px", fontSize: 13, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

/* Split a free-form string into individual email addresses. */
function parseExtraEmails(raw) {
  if (!raw) return [];
  return String(raw)
    .split(/[\s,;]+/g)
    .map(s => s.trim())
    .filter(Boolean);
}

export default function EmailDialog({
  lang,
  title,
  recipients,        /* [{ name, email, checked }] — initial state */
  docOptions,        /* [{ key, label, checked }] */
  defaultSubject,
  defaultBody,
  fromEmail,         /* default/fallback from address */
  emailAccounts,     /* [{ id, name, email, signature }] — multiple sending accounts from CompanyProfile */
  buildAttachments,  /* async (checkedKeys: string[]) => [{ filename, blob, mimeType }] */
  onClose,
  onSent,            /* optional callback after successful send */
  /* kindOptions: optional radio group rendered above Subject. Each
     option has { value, label, subject, docKeyOn?, docKeyOff? }.
     When the user picks one, `subject` is replaced with the option's
     `subject` template AND (if docKeyOn/docKeyOff are set) the
     matching docs are toggled — used by Invoice for the
     Inbound/Pick Ticket switch. */
  kindOptions,
  defaultKind,       /* initial value for the kindOptions radio */
  /* signatures: [{ id, name, html, isDefault? }] — saved HTML
     signatures from CompanyProfile. The dialog shows a picker
     ("(none)" + each named signature); the selected one is appended
     to the message HTML on send. */
  signatures,
  defaultSignatureId, /* preselected signature; falls back to the one flagged isDefault. */
  /* Optional: async (checkedDocKeys: string[]) => string. When set,
     the dialog calls it after the user clicks Send and embeds the
     returned HTML directly inside the email body (above the
     signature). Used so the customer/warehouse sees the doc inline
     without opening the PDF attachment. */
  buildEmbedHtml,
  /* Optional node rendered at the top of the form, above Recipients.
     Callers inject flow-specific pickers here (e.g. the certification
     client/contact selector). Presentation-only — the caller owns the
     state and feeds the result back through `recipients`. */
  topContent,
  hideDocs = false,
  /* When true, always call buildAttachments (even if "Attach PDF" is
     off or no docs are checked) and skip the noAttachments guard —
     used when the caller injects a REQUIRED attachment inside
     buildAttachments (e.g. the Organic Certificates Summary PDF). */
  alwaysBuildAttachments,
  /* When non-empty, the Send button is disabled and this text is shown
     as the reason (e.g. "select a client and contact first"). */
  sendDisabledReason,
  /* Optional pair: when `body` (live, as the user is editing it) still
     contains `blockIfBodyContains`, Send stays disabled with
     `blockIfBodyContainsMessage` as the reason — same treatment as
     sendDisabledReason, but reactive to the textarea instead of a
     static prop. Built for a mandatory placeholder the caller wants
     replaced with real content before sending (2026-09-14, Arturo,
     Quality Report email: "por default debe decir 'Pega aqui
     comentarios del equipo de calidad' si no cambia esa leyenda no se
     habilita enviar"). */
  blockIfBodyContains,
  blockIfBodyContainsMessage,
  /* Trusted HTML string inserted between the message body (and doc
     embed, if any) and the signature. See the "Customer Remarks"
     comment in handleSend below for the motivating use case. */
  insertBeforeSignatureHtml,
  /* Trusted HTML string inserted BEFORE the editable message body —
     the mirror of insertBeforeSignatureHtml, for content that must
     render first (2026-10-16, Dump Order — Shipper: a warning banner
     + reference/pallet-detail box that must appear ahead of whatever
     Arturo types/edits in the Message field, which stays free for him
     to add his own remarks without touching the styled block above
     it). Never shown in the plain-text Message textarea itself (same
     as insertBeforeSignatureHtml) — only visible via the dialog's own
     "Preview" button or in the actual sent email. */
  prependHtml,
  /* Optional: async () => accounts[] — overrides handleSend's default
     "refetch company_profile" step for resolving the freshest
     maskedFrom/signature at send time. Required for any caller whose
     emailAccounts DON'T come from company_profile (e.g.
     SweetLeafPortal.js, which uses sweetleaf_settings) — see the
     2026-09-14 bug note in handleSend. */
  refreshAccountsBeforeSend,
  /* Optional: (html: string, resolvedFromEmail: string) => string,
     called on the final HTML body right before sendEmail — AFTER the
     signature has been appended, the last step before the network
     call. `resolvedFromEmail` is the account this send is actually
     going out from (its maskedFrom alias if set, else its real
     address) so a caller can vary what it appends by account — e.g.
     emailHelpers.js's appendDisclosureByAccount picking GF vs
     SweetLeaf's disclosure. Runs even when there's otherwise no
     signature/embed (so a caller relying on this to always run
     doesn't silently get skipped) — undefined by default, zero
     behavior change for callers that don't pass it. */
  postProcessHtmlBody,
  /* Optional: override the DraggableModal's z-index. Needed when this
     dialog is opened NESTED inside another already-stacked modal (e.g.
     EmailSalesReportModal opened from inside SalesReportPdfPreview,
     which itself sits above its own DraggableModal parent) — each
     layer needs a strictly higher z-index than the one below it.
     Defaults to 10003, the value every other caller already relied on
     implicitly (above the navbar's 9000/10000 and the base
     DraggableModal's 10001). */
  zIndex,
}) {
  const t = T[lang === "es" ? "es" : "en"];
  const [recips, setRecips] = useState(() => (recipients || []).map(r => ({ ...r })));
  const [extra, setExtra] = useState("");
  const [docs, setDocs] = useState(() => (docOptions || []).map(d => ({ ...d })));
  const [subject, setSubject] = useState(defaultSubject || "");
  const [body, setBody] = useState(defaultBody || "");
  /* Default ON — Arturo's workflow is "send with the PDFs attached".
     Toggling OFF skips attachment generation, useful when the docs
     are huge or only the message body matters. Off by default when
     opening directly into a `noAttachments` kind (e.g. Pickup
     Reminder) — handleKindChange keeps this in sync on later changes,
     this only covers the initial mount, since defaultKind seeds
     kindValue via useState and never runs through handleKindChange. */
  const [attachPdf, setAttachPdf] = useState(() => {
    if (Array.isArray(kindOptions) && defaultKind) {
      var initialOpt = kindOptions.find(o => o.value === defaultKind);
      if (initialOpt && initialOpt.noAttachments) return false;
    }
    /* No docOptions and no required-attachment mode means there is
       nothing this dialog could possibly attach (2026-10-12, Weather
       Watch's alert — no documents exist for this send at all) —
       start unchecked rather than defaulting to a toggle that does
       nothing (or, if buildAttachments wasn't even provided, crashes
       handleSend). */
    if (!(Array.isArray(docOptions) && docOptions.length > 0) && !alwaysBuildAttachments) return false;
    return true;
  });
  /* Default ON — Arturo always wants a copy in his own inbox so the
     thread is searchable later. CC goes to the active From account's
     real address (selectedFromEmail/fromEmail below). 2026-09-28
     (Arturo: "tengo esa opcion... pero nunca aparece el envio en mi
     outbound") — the label used to promise this lands in his Gmail
     Outbound/Sent, which can never happen: sends go through Resend,
     not Arturo's own Google account, so a CC always arrives as a
     RECEIVED message in his Inbox, never in Sent — Gmail only
     populates Sent for mail it personally dispatched. Relabeled
     (sendCopyHint) to say that plainly instead of promising a folder
     this mechanism can't reach. */
  const [sendCopy, setSendCopy] = useState(true);
  const [kindValue, setKindValue] = useState(defaultKind || (kindOptions && kindOptions[0] && kindOptions[0].value) || "");
  /* Signature picker. "" means "no signature appended" — useful when
     the user wants a clean reply or a one-off message. Skips both the
     defaultSignatureId prop AND the isDefault fallback when the
     initial kind is flagged `noSignatureByDefault` (2026-09-25,
     Arturo: pickup/protection reminders "no deben llevar mi firma
     solo el nombre de sweetleaf... por default que este sin firma con
     la opcion de yo agregarla") — same initial-mount-only pattern as
     attachPdf's noAttachments check above; the user can still pick a
     signature by hand from the dropdown below. */
  const [signatureId, setSignatureId] = useState(() => {
    if (Array.isArray(kindOptions) && defaultKind) {
      var initialOpt = kindOptions.find(o => o.value === defaultKind);
      if (initialOpt && initialOpt.noSignatureByDefault) return "";
    }
    if (defaultSignatureId) return String(defaultSignatureId);
    const def = (signatures || []).find(s => s && s.isDefault);
    return def ? String(def.id) : "";
  });
  const [selectedFromEmail, setSelectedFromEmail] = useState(() => {
    if (emailAccounts && emailAccounts.length > 0) {
      var match = fromEmail ? emailAccounts.find(a => a.email === fromEmail) : null;
      return match ? match.email : emailAccounts[0].email;
    }
    return fromEmail || "";
  });
  const bodyBlockedReason = (blockIfBodyContains && body.indexOf(blockIfBodyContains) >= 0) ? (blockIfBodyContainsMessage || "") : "";
  const effectiveDisabledReason = sendDisabledReason || bodyBlockedReason;
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState(""); /* "preparing" | "sending" */
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  /* 2026-09-28 (Arturo: "te das cuenta que como no puedo ver la firma
     la unica manera es enviando un email y esperar a que me llegue?")
     — the signature (forced account signature, or the picked one),
     the Confidentiality Notice (postProcessHtmlBody), and any
     doc-embed table were only ever assembled inside handleSend, at
     the moment of actually sending — there was no way to see the real
     final HTML beforehand. New Preview button (below) builds the
     exact same HTML via buildRenderedHtml() and shows it in an
     iframe, without sending anything. */
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const extraList = useMemo(() => parseExtraEmails(extra), [extra]);
  const checkedRecipEmails = useMemo(() => recips.filter(r => r.checked).map(r => r.email).filter(Boolean), [recips]);
  const allEmails = useMemo(() => Array.from(new Set([...checkedRecipEmails, ...extraList].map(s => s.trim()).filter(Boolean))), [checkedRecipEmails, extraList]);
  const invalidEmails = useMemo(() => allEmails.filter(e => !isValidEmail(e)), [allEmails]);
  const checkedDocs = useMemo(() => docs.filter(d => d.checked).map(d => d.key), [docs]);

  /* `locked` rows can't be toggled off — used for required recipients
     (e.g. the admin on a shipper-portal Bill of Lading email). They
     always render as checked + disabled and ignore Deselect-all. */
  const toggleRecip = (idx) => setRecips(prev => prev.map((r, i) => i === idx ? (r.locked ? r : { ...r, checked: !r.checked }) : r));
  const toggleDoc = (idx) => setDocs(prev => prev.map((d, i) => i === idx ? { ...d, checked: !d.checked } : d));

  /* Select-all booleans. `all*Checked` is true only when every row is
     checked (so the checkbox shows ✓ vs blank); `any*Checked` drives
     the indeterminate state visually. */
  const allRecipsChecked = recips.length > 0 && recips.every(r => r.checked);
  const allDocsChecked = docs.length > 0 && docs.every(d => d.checked);
  const setAllRecips = (value) => setRecips(prev => prev.map(r => (r.locked ? { ...r, checked: true } : { ...r, checked: value })));
  const setAllDocs = (value) => setDocs(prev => prev.map(d => ({ ...d, checked: value })));

  /* Kind-options radio: when the user picks one, replace the Subject
     (and optionally the Body) with that option's template, and
     toggle the docs identified by docKeyOn/docKeyOff. Manual
     checkbox / subject / body edits afterwards still win — this is
     a one-way push, not a continuous bind. */
  const handleKindChange = (newValue) => {
    if (!Array.isArray(kindOptions)) return;
    const opt = kindOptions.find(o => o.value === newValue);
    if (!opt) return;
    setKindValue(newValue);
    if (opt.subject != null) setSubject(opt.subject);
    if (opt.body != null) setBody(opt.body);
    setDocs(prev => prev.map(d => {
      if (opt.docKeyOn && d.key === opt.docKeyOn) return { ...d, checked: true };
      if (opt.docKeyOff && d.key === opt.docKeyOff) return { ...d, checked: false };
      return d;
    }));
    /* Kinds like "Pickup Reminder" are a plain text nudge, never a
       document send (2026-09-12, Arturo: "pick up reminder no lleva
       anexos, solo el mensaje de recordatorio") — force the toggle
       off (and the render below disables it) rather than leaving it
       to whatever the user had picked for the previous kind. */
    setAttachPdf(!opt.noAttachments);
  };

  /* 2026-09-28 — extracted out of handleSend so the new Preview button
     can build the EXACT same final HTML (doc embed, signature,
     Confidentiality Notice / postProcessHtmlBody) without sending
     anything. Every comment on the account-refresh / signature-
     precedence logic below is unchanged from when this lived inline
     in handleSend — see git history for the original context if
     needed, kept short here to avoid duplicating the same essay in
     two places. `forPreview` skips the `buildEmbedHtml` gate's
     dependency on `attachPdf` being meaningfully "on" for a real
     send — it still only builds the embed when there's something
     checked, same as a real send would. */
  const buildRenderedHtml = async () => {
    let docEmbedHtml = "";
    if (attachPdf && checkedDocs.length > 0 && typeof buildEmbedHtml === "function") {
      try { docEmbedHtml = await buildEmbedHtml(checkedDocs); }
      catch (e) { console.warn("[EmailDialog] buildEmbedHtml failed:", e && e.message); docEmbedHtml = ""; }
    }
    /* Resolve the active email account fresh from Supabase — see
       2026-09-12/2026-09-14 history in this file's earlier comments
       (git blame) for why this can't just trust the emailAccounts
       prop. */
    let freshAccounts = emailAccounts;
    try {
      if (typeof refreshAccountsBeforeSend === "function") {
        const custom = await refreshAccountsBeforeSend();
        if (Array.isArray(custom) && custom.length > 0) freshAccounts = custom;
      } else {
        const freshRows = await loadFromSupabase("company_profile");
        const freshCompany = (freshRows || [])[0];
        if (freshCompany && Array.isArray(freshCompany.emailAccounts) && freshCompany.emailAccounts.length > 0) {
          freshAccounts = freshCompany.emailAccounts;
        }
      }
    } catch (e) { /* keep the emailAccounts prop as fallback */ }
    const activeAcct = (freshAccounts || []).find(function (a) { return a.email === (selectedFromEmail || fromEmail); });
    /* The account's own signature normally always wins over the
       picker below (a deliberate "every send from this account gets
       signed" default) — but a kind flagged `noSignatureByDefault`
       (pickup/protection reminders, 2026-09-25) opts out of that
       forced signature entirely, falling through to whatever the
       user picked (or left blank) in the Signature dropdown. */
    const currentKindOpt = (kindOptions || []).find(o => o.value === kindValue);
    const suppressAcctSig = !!(currentKindOpt && currentKindOpt.noSignatureByDefault);
    var acctSig = "";
    if (!suppressAcctSig && activeAcct && activeAcct.signature) acctSig = activeAcct.signature;
    const selectedSig = !acctSig ? (signatures || []).find(s => String(s.id) === String(signatureId)) : null;
    const sigHtml = acctSig || (selectedSig && selectedSig.html ? selectedSig.html : "");
    let htmlBody = null;
    if (sigHtml || docEmbedHtml || insertBeforeSignatureHtml || prependHtml || typeof postProcessHtmlBody === "function") {
      const escape = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const messageHtml = escape(body).replace(/\n/g, "<br>");
      htmlBody = (prependHtml || "") + '<div style="font-family:Arial,sans-serif;font-size:13px;color:#1a1a1a;line-height:1.5">' + messageHtml + '</div>';
      if (docEmbedHtml) htmlBody += '<br><hr style="border:none;border-top:1px solid #d0d5dd;margin:20px 0"><br>' + docEmbedHtml;
      /* Optional caller-supplied HTML block inserted between the
         message/doc-embed and the signature — e.g. InvoiceRegistry.js's
         Protection Notice "Customer Remarks" (2026-09-14, Arturo:
         pasted verbatim from the client's own email, images and all,
         must land above his signature, not below it). Trusted HTML
         (built by the caller, not raw user input passed through
         unescaped) — same trust level as buildEmbedHtml's output. */
      if (insertBeforeSignatureHtml) htmlBody += insertBeforeSignatureHtml;
      if (sigHtml) htmlBody += '<br><br>' + sigHtml;
      if (typeof postProcessHtmlBody === "function") {
        /* Same resolution as fromForSend below (masked alias when the
           account has one, else the real/selected address) — passed
           so a caller like appendDisclosureByAccount can tell which
           brand's account this send is actually going out from. */
        const resolvedFrom = activeAcct && activeAcct.maskedFrom ? activeAcct.maskedFrom : (selectedFromEmail || fromEmail);
        htmlBody = postProcessHtmlBody(htmlBody, resolvedFrom);
      }
    }
    /* Resolve maskedFrom and replyTo from the active email account.
       The user-facing From is the @gfinexus.com alias; Reply-To is
       the real email so recipients can respond directly to Arturo
       (or to SweetLeaf, etc.) without bouncing through Resend. */
    const fromForSend = activeAcct && activeAcct.maskedFrom ? activeAcct.maskedFrom : (selectedFromEmail || fromEmail);
    const replyToAddr = activeAcct ? activeAcct.email : (selectedFromEmail || fromEmail);
    return { htmlBody, fromForSend, replyToAddr };
  };

  const handlePreview = async () => {
    if (previewBusy) return;
    setPreviewError(""); setPreviewBusy(true);
    try {
      const { htmlBody } = await buildRenderedHtml();
      const escape = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const fallback = '<div style="font-family:Arial,sans-serif;font-size:13px;color:#1a1a1a;line-height:1.5;white-space:pre-wrap">' + escape(body) + '</div>';
      setPreviewHtml(htmlBody || fallback);
    } catch (e) {
      setPreviewError(t.error + ": " + (e && e.message ? e.message : String(e)));
    } finally {
      setPreviewBusy(false);
    }
  };

  const handleSend = async () => {
    if (busy) return;
    setError("");
    if (effectiveDisabledReason) { setError(effectiveDisabledReason); return; }
    if (allEmails.length === 0) { setError(t.noRecipients); return; }
    if (invalidEmails.length > 0) { setError(t.invalidEmail + ": " + invalidEmails.join(", ")); return; }
    /* When the user opts out of attachments, skip the noAttachments
       guard — they may genuinely want to send a message-only email.
       Also skip it in alwaysBuildAttachments mode, since a required
       attachment (e.g. the summary PDF) is added regardless. And skip
       it entirely when the caller passed no docOptions at all (2026-
       10-12, Arturo: Weather Watch's alert — no documents exist to
       attach in the first place, so the default-checked "Attach PDF"
       box should never be able to block Send with "select at least
       one document" when there was never anything to select). */
    const hasDocOptions = Array.isArray(docOptions) && docOptions.length > 0;
    if (!alwaysBuildAttachments && hasDocOptions && attachPdf && checkedDocs.length === 0) { setError(t.noAttachments); return; }
    setBusy(true); setPhase("preparing");
    try {
      /* Cert docs honour the attachPdf toggle; the required attachment
         is added inside buildAttachments regardless. Pass [] for docs
         when attachPdf is off so only the required attachment builds.
         Defensive: a caller with nothing to attach (no docOptions, no
         alwaysBuildAttachments) never passes buildAttachments at all —
         the UI already hides the toggle and defaults it off for that
         case, but guard here too rather than crash if attachPdf
         somehow ends up true anyway. */
      const attachments = (attachPdf || alwaysBuildAttachments) && typeof buildAttachments === "function"
        ? await buildAttachments(attachPdf ? checkedDocs : [])
        : [];
      setPhase("sending");
      /* "Send myself a copy" → CC the active From account's real
         address so Arturo gets the sent thread as a message in his own
         Inbox, without bcc'ing everyone. Falls back to nothing if no
         From account is resolved. */
      const activeFrom = selectedFromEmail || fromEmail || "";
      const cc = sendCopy && activeFrom ? [activeFrom] : [];
      const { htmlBody, fromForSend, replyToAddr } = await buildRenderedHtml();

      const result = await sendEmail({
        to: allEmails,
        cc: cc,
        from: fromForSend,
        replyTo: replyToAddr,
        subject: subject,
        text: body,
        html: htmlBody || undefined,
        attachments: attachments,
      });
      setDone(true);
      /* Enriched beyond the raw sendEmail() result — every EXISTING
         onSent caller (14+ across the app) uses a zero-arg callback
         and never reads this argument, so widening it is backward-
         compatible. Added for fix-dashboard.md Fase 3's send-tracking
         columns (BillRegistry "Report Sent" / PaymentRegistry
         "Confirmation Sent"), which need the ACTUAL resolved to/from/
         subject/attachments of this send — not a best-guess
         reconstructed from the caller's own pre-send state, which
         could differ (unchecked recipients, manually-added extra
         emails, the resolved maskedFrom alias). */
      if (typeof onSent === "function") onSent({ result: result, to: allEmails, from: fromForSend, subject: subject, attachmentNames: (attachments || []).map(function (a) { return a.filename; }) });
    } catch (e) {
      console.error("[EmailDialog] send failed", e);
      setError(t.error + ": " + (e.message || String(e)));
    } finally {
      setBusy(false);
      setPhase("");
    }
  };

  return (
    <DraggableModal title={title || t.title} onClose={onClose} initialWidth={680} initialHeight={Math.min(720, window.innerHeight - 60)} zIndex={zIndex || 10003}>
      <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}>
        {(emailAccounts && emailAccounts.length > 1) ? (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{t.fromAccount}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 6 }}>
              {emailAccounts.map(acct => {
                const isActive = (selectedFromEmail || fromEmail) === acct.email;
                return (
                  <button
                    key={acct.id || acct.email}
                    type="button"
                    onClick={() => setSelectedFromEmail(acct.email)}
                    style={{
                      textAlign: "left",
                      padding: "8px 10px",
                      borderRadius: 6,
                      border: "1px solid " + (isActive ? "var(--color-accent)" : "var(--color-border)"),
                      background: isActive ? "rgba(42,122,111,0.08)" : "transparent",
                      color: "var(--color-text)",
                      cursor: "pointer",
                      fontSize: 12,
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>{acct.name || "—"}</div>
                    <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>{acct.email}</div>
                    {acct.signature ? <div style={{ fontSize: 9, color: "var(--color-accent)", marginTop: 2 }}>✓ {lang === "es" ? "con firma" : "has signature"}</div> : null}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (selectedFromEmail || fromEmail) ? (() => {
          /* Prefer the account's display name (e.g. "Paulo González",
             "Ground-Fresh") so the user doesn't see the @gfinexus.com
             masked alias. Falls back to the raw email when no account
             matches. */
          const activeAcct = (emailAccounts || []).find(function (a) { return a.email === (selectedFromEmail || fromEmail); });
          const displayFrom = activeAcct && activeAcct.name ? activeAcct.name : (selectedFromEmail || fromEmail);
          return (
            <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 12, fontFamily: "'JetBrains Mono', monospace" }}>
              From: {displayFrom}
            </div>
          );
        })() : null}

        {topContent ? <div style={{ marginBottom: 14 }}>{topContent}</div> : null}

        {/* Recipients */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.recipients}</span>
            {recips.length > 1 && (
              <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--color-text-muted)", cursor: "pointer", fontWeight: 600 }}>
                <input type="checkbox" checked={allRecipsChecked} onChange={(e) => setAllRecips(e.target.checked)} />
                {allRecipsChecked ? t.deselectAll : t.selectAll}
              </label>
            )}
          </div>
          {recips.length > 0 ? (
            <div style={{ border: "1px solid var(--color-border)", borderRadius: 6, padding: 8, background: "var(--color-bg)" }}>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 6, fontStyle: "italic" }}>{t.fromDistribution}</div>
              {recips.map((r, i) => (
                <label key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: r.locked ? "default" : "pointer", fontSize: 12 }}>
                  <input type="checkbox" checked={!!r.checked} onChange={() => toggleRecip(i)} disabled={!!r.locked} />
                  <span style={{ flex: 1 }}>
                    {r.name ? <strong>{r.name}</strong> : null}
                    {r.name ? <span style={{ color: "var(--color-text-muted)" }}> · </span> : null}
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text)" }}>{r.email}</span>
                    {r.locked && <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 8, background: "rgba(42,122,111,0.12)", color: "var(--color-accent)", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{lang === "es" ? "Requerido" : "Required"}</span>}
                  </span>
                </label>
              ))}
            </div>
          ) : null}
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 4 }}>{t.addMore}</div>
            <textarea value={extra} onChange={e => setExtra(e.target.value)} rows={2} style={{ ...inp, resize: "vertical", fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }} placeholder="name@example.com, other@example.com" />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2, fontStyle: "italic" }}>{t.addMoreHint}</div>
          </div>
        </div>

        {/* Docs */}
        {!hideDocs && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.docs}</span>
            {docs.length > 1 && attachPdf && (
              <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--color-text-muted)", cursor: "pointer", fontWeight: 600 }}>
                <input type="checkbox" checked={allDocsChecked} onChange={(e) => setAllDocs(e.target.checked)} />
                {allDocsChecked ? t.deselectAll : t.selectAll}
              </label>
            )}
          </div>
          {docs.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>—</div>
          ) : (
            <div style={{ border: "1px solid var(--color-border)", borderRadius: 6, padding: 8, background: "var(--color-bg)", opacity: attachPdf ? 1 : 0.5 }}>
              {docs.map((d, i) => (
                <label key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: attachPdf ? "pointer" : "not-allowed", fontSize: 12 }}>
                  <input type="checkbox" checked={!!d.checked} onChange={() => toggleDoc(i)} disabled={!attachPdf} />
                  <span>{d.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Kind selector (Pick Ticket vs Inbound for invoice emails). */}
        {Array.isArray(kindOptions) && kindOptions.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{t.docType}</div>
            <div style={{ display: "flex", gap: 14 }}>
              {kindOptions.map(opt => (
                <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                  <input type="radio" name="email-kind" value={opt.value} checked={kindValue === opt.value} onChange={() => handleKindChange(opt.value)} />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Options */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{t.options}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(() => {
              /* A noAttachments kind (e.g. Pickup Reminder) is a plain
                 text nudge, never a document send — lock the toggle
                 off and greyed out so it can't be re-enabled while
                 that kind is selected (2026-09-12, Arturo: "pick up
                 reminder no lleva anexos, solo el mensaje de
                 recordatorio"). */
              const currentKindOpt = Array.isArray(kindOptions) ? kindOptions.find(o => o.value === kindValue) : null;
              const attachLocked = !!(currentKindOpt && currentKindOpt.noAttachments);
              /* No docOptions and no required-attachment mode means
                 there is nothing to attach at all for this dialog
                 (2026-10-12, Weather Watch's alert) — the toggle would
                 do nothing (or crash handleSend if buildAttachments
                 wasn't even provided), so it's hidden entirely rather
                 than shown disabled/no-op. */
              const hasAnythingToAttach = (Array.isArray(docOptions) && docOptions.length > 0) || alwaysBuildAttachments;
              if (!hasAnythingToAttach) return null;
              return (
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: attachLocked ? "not-allowed" : "pointer", opacity: attachLocked ? 0.5 : 1 }}>
                  <input type="checkbox" checked={attachPdf} onChange={e => setAttachPdf(e.target.checked)} disabled={attachLocked} />
                  <span>{t.attachPdf}</span>
                </label>
              );
            })()}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, cursor: (selectedFromEmail || fromEmail) ? "pointer" : "not-allowed", opacity: (selectedFromEmail || fromEmail) ? 1 : 0.5 }}>
              <input type="checkbox" checked={sendCopy} onChange={e => setSendCopy(e.target.checked)} disabled={!(selectedFromEmail || fromEmail)} style={{ marginTop: 3 }} />
              <span>
                {t.sendCopy}
                {(selectedFromEmail || fromEmail) && <span style={{ display: "block", fontSize: 10, color: "var(--color-text-muted)", marginTop: 1, fontFamily: "'JetBrains Mono', monospace" }}>{t.sendCopyHint} · {selectedFromEmail || fromEmail}</span>}
              </span>
            </label>
          </div>
        </div>

        {/* Subject */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{t.subject}</div>
          <input type="text" value={subject} onChange={e => setSubject(e.target.value)} style={inp} />
        </div>

        {/* Body */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{t.body}</div>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={5} style={{ ...inp, resize: "vertical" }} />
        </div>

        {/* Signature picker — only shown when CompanyProfile has at
            least one saved signature. The selected one is appended
            to the message as HTML on send. */}
        {Array.isArray(signatures) && signatures.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{t.signature}</div>
            <select value={signatureId} onChange={e => setSignatureId(e.target.value)} style={{ ...inp, fontFamily: "inherit" }}>
              <option value="">{t.signatureNone}</option>
              {signatures.map(s => (
                <option key={s.id} value={String(s.id)}>{s.name || "—"}{s.isDefault ? " ★" : ""}</option>
              ))}
            </select>
            {(function () {
              const sel = signatures.find(s => String(s.id) === String(signatureId));
              if (!sel || !sel.html) return null;
              return (
                <div style={{ marginTop: 6 }}>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 3, fontStyle: "italic" }}>{t.signaturePreview}</div>
                  <div style={{ padding: 10, borderRadius: 6, border: "1px dashed var(--color-border)", background: "#ffffff", color: "#1a1a1a" }} dangerouslySetInnerHTML={{ __html: sel.html }} />
                </div>
              );
            })()}
          </div>
        )}

        {previewError && (
          <div style={{ padding: "10px 12px", borderRadius: 6, background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontSize: 12, marginBottom: 12 }}>
            {previewError}
          </div>
        )}
        {error && (
          <div style={{ padding: "10px 12px", borderRadius: 6, background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontSize: 12, marginBottom: 12 }}>
            {error}
          </div>
        )}
        {done && !error && (
          <div style={{ padding: "10px 12px", borderRadius: 6, background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.30)", color: "#047857", fontSize: 12, fontWeight: 700, marginBottom: 12 }}>
            ✓ {t.sentMsg}
          </div>
        )}
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
          {busy && phase === "preparing" && (t.preparing)}
          {busy && phase === "sending" && (t.sending)}
          {!busy && effectiveDisabledReason ? <span style={{ color: "#B7791F", fontWeight: 600 }}>{effectiveDisabledReason}</span> : null}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {!done && (
            <button onClick={handlePreview} disabled={previewBusy} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: previewBusy ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 500, opacity: previewBusy ? 0.7 : 1 }}>
              👁 {previewBusy ? t.previewLoading : t.preview}
            </button>
          )}
          <button onClick={onClose} disabled={busy} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: busy ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 500 }}>{done ? t.cancel : t.cancel}</button>
          {!done && (
            <button onClick={handleSend} disabled={busy || !!effectiveDisabledReason} style={{ padding: "8px 22px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: (busy || effectiveDisabledReason) ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, opacity: (busy || effectiveDisabledReason) ? 0.7 : 1 }}>
              {busy ? t.sending : t.send}
            </button>
          )}
        </div>
      </div>
      {previewHtml !== null && createPortal(
        /* zIndex one above this dialog's own (default 10003, or
           `zIndex` prop when the caller nested this INSIDE another
           overlay, e.g. AuditReportPreviewModal's Send-by-Email at
           10004) — same "portaled siblings stack by z-index, not tree
           nesting" reasoning documented elsewhere in this app
           (SalesReports.js's SalesReportPdfPreview). */
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: (zIndex || 10003) + 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: "min(680px, 92vw)", height: "80vh", background: "#fff", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "12px 20px", background: "var(--color-bg-card-solid)", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 14, fontWeight: 700 }}>{t.previewTitle}</span>
              <button onClick={() => setPreviewHtml(null)} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.previewClose}</button>
            </div>
            <iframe title="email-preview" srcDoc={previewHtml} style={{ flex: 1, border: "none", background: "#fff" }} />
          </div>
        </div>,
        document.body
      )}
    </DraggableModal>
  );
}
